import { EDispatchNoteDesign } from "dto/Printing/IOutgoingPrint";

// Ustawienia stanowiska do drukowania przesyłek — w Magistracie trzymane w user.config
// (PrintSettings: format, profil, papier, opcje per kategoria), tu w pamięci przeglądarki
// użytkownika. Drukarkę i dwustronność wybiera się w oknie drukowania przeglądarki.

export type PaperKey =
  | "format"
  | "A4"
  | "A5"
  | "A6"
  | "DL"
  | "C6"
  | "C5"
  | "C4"
  | "ZPO"
  | "custom";

export interface IPaperSize {
  key: PaperKey;
  label: string;
  width?: number;
  height?: number;
}

/** Papier w podajniku (pionowo, jak w sterownikach drukarek). */
export const PAPER_SIZES: IPaperSize[] = [
  { key: "format", label: "Jak wydruk (bez dopasowania)" },
  { key: "A4", label: "A4 (210 × 297 mm)", width: 210, height: 297 },
  { key: "A5", label: "A5 (148 × 210 mm)", width: 148, height: 210 },
  { key: "A6", label: "A6 (105 × 148 mm)", width: 105, height: 148 },
  { key: "DL", label: "Koperta DL (110 × 220 mm)", width: 110, height: 220 },
  { key: "C6", label: "Koperta C6 (114 × 162 mm)", width: 114, height: 162 },
  { key: "C5", label: "Koperta C5 (162 × 229 mm)", width: 162, height: 229 },
  { key: "C4", label: "Koperta C4 (229 × 324 mm)", width: 229, height: 324 },
  { key: "ZPO", label: "Druk ZPO (101 × 160 mm)", width: 101, height: 160 },
  { key: "custom", label: "Własny rozmiar" },
];

export interface IPaperChoice {
  paper: PaperKey;
  customWidth?: number | null;
  customHeight?: number | null;
}

export interface IParcelPrintOptions {
  caseSignature: boolean;
  parcelSignature: boolean;
  title: boolean;
}

export interface IStickerPrefs extends IPaperChoice, IParcelPrintOptions {
  formatId?: string | null;
  profileId?: string | null;
  fontSize: number;
  quantity: number;
  sheet: boolean;
  landscape: boolean;
  skip: number;
  qr: boolean;
}

export interface IEnvelopePrefs extends IPaperChoice, IParcelPrintOptions {
  formatId?: string | null;
  profileId?: string | null;
  sender: boolean;
}

export interface IDispatchNotePrefs extends IPaperChoice, IParcelPrintOptions {
  profileId?: string | null;
  design: EDispatchNoteDesign;
  qr: boolean;
}

export interface ILetterPrefs {
  duplex: boolean;
}

/** Kategorie okna drukowania (PrintCategories z Magistratu, bez załączników/metryk/rozdzielnika). */
export type PrintCategoryKey = "letters" | "adActa" | "stickers" | "envelopes" | "dispatchNotes";

/** Miejsce, z którego otwarto drukowanie — każde pamięta własny ostatni wybór kategorii. */
export type PrintContext = "projects" | "outgoingLetters" | "mailroom";

export interface IPrintPreferences {
  letters: ILetterPrefs;
  stickers: IStickerPrefs;
  envelopes: IEnvelopePrefs;
  dispatchNotes: IDispatchNotePrefs;
  selection: Partial<Record<PrintContext, PrintCategoryKey[]>>;
}

// Wartości domyślne jak w Magistracie (ParcelPrintOptions.Default: sygnatura teczki i przesyłki;
// naklejki 12 pt, 1 kopia; zwrotka żółta). Naklejki domyślnie arkuszem — domyślny format to
// arkusz A4 3 × 8.
export const DEFAULT_PRINT_PREFERENCES: IPrintPreferences = {
  letters: { duplex: false },
  stickers: {
    formatId: null,
    profileId: null,
    paper: "format",
    fontSize: 12,
    quantity: 1,
    sheet: true,
    landscape: false,
    skip: 0,
    qr: false,
    caseSignature: true,
    parcelSignature: true,
    title: false,
  },
  envelopes: {
    formatId: null,
    profileId: null,
    paper: "format",
    sender: false,
    caseSignature: true,
    parcelSignature: true,
    title: false,
  },
  dispatchNotes: {
    profileId: null,
    paper: "format",
    design: EDispatchNoteDesign.Yellow,
    qr: true,
    caseSignature: true,
    parcelSignature: true,
    title: false,
  },
  // Projekty i wychodzące — pisma do podpisu/koperty autora; biuro podawcze wybiera samo
  // (w Magistracie żadna kategoria nie była zaznaczona).
  selection: {
    projects: ["letters"],
    outgoingLetters: ["letters"],
    mailroom: [],
  },
};

const storageKey = (userId: string) => `nota.print.outgoing.${userId || "anon"}`;

/** Odczyt ustawień stanowiska; brak/uszkodzone/niedostępne → domyślne. */
export const loadPrintPreferences = (userId: string): IPrintPreferences => {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return DEFAULT_PRINT_PREFERENCES;
    const saved = JSON.parse(raw) as Partial<IPrintPreferences>;
    return {
      letters: { ...DEFAULT_PRINT_PREFERENCES.letters, ...saved.letters },
      stickers: { ...DEFAULT_PRINT_PREFERENCES.stickers, ...saved.stickers },
      envelopes: { ...DEFAULT_PRINT_PREFERENCES.envelopes, ...saved.envelopes },
      dispatchNotes: { ...DEFAULT_PRINT_PREFERENCES.dispatchNotes, ...saved.dispatchNotes },
      selection: { ...DEFAULT_PRINT_PREFERENCES.selection, ...saved.selection },
    };
  } catch {
    return DEFAULT_PRINT_PREFERENCES;
  }
};

export const savePrintPreferences = (userId: string, prefs: IPrintPreferences) => {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
    return true;
  } catch {
    return false;
  }
};

/** Papier w mm dla żądania wydruku; null = strona o rozmiarze wydruku. */
export const resolvePaper = (choice: IPaperChoice): { width: number; height: number } | null => {
  if (choice.paper === "custom") {
    const w = Number(choice.customWidth);
    const h = Number(choice.customHeight);
    return w > 0 && h > 0 ? { width: w, height: h } : null;
  }
  const size = PAPER_SIZES.find((p) => p.key === choice.paper);
  return size?.width && size?.height ? { width: size.width, height: size.height } : null;
};
